import React from 'react';
import moment from 'moment';

import formatDate from '../../../helpers/formatDate';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import { DEFAULT_MAXIMUM_REGISTRATION_AMOUNT } from '../../../config/constants';
import CoreLink from '../../CoreLink';

import './style.scss';

/**
 * As in: 100%
 */
const FULL_WIDTH_BAR_OFFSET = 100;
interface Props {
  date: Date;
  max?: number;
  value?: number;
  goal?: number;
  readonly?: boolean;
}

type GoalAchievementState = 'NO_DATA' | 'GOAL_ACHIEVED' | 'GOAL_NOT_ACHIEVED' | 'GOAL_NOT_SET';

const BarGraphStaff: React.FC<Props> = ({
  date,
  max = DEFAULT_MAXIMUM_REGISTRATION_AMOUNT,
  value,
  goal,
  readonly,
}) => {
  const formattedDayOfWeek = formatDate(date, 'dd');

  const formattedDate = formatDate(date, 'D MMMM YYYY');

  const today = moment();

  const isToday = today.isSame(date, 'day');

  const isInFuture = today.isBefore(date, 'day');

  const routes = useRoutes();

  const goalAchievementState = React.useMemo((): GoalAchievementState => {
    if (value == null) {
      return 'NO_DATA';
    }

    if (goal == null) {
      return 'GOAL_NOT_SET';
    }

    if (value <= goal) {
      return 'GOAL_ACHIEVED';
    }

    return 'GOAL_NOT_ACHIEVED';
  }, [value, goal]);

  const isEditable = !isInFuture && !readonly;

  const valueProportion = value ? value / max : 0;
  const goalProportion = goal / max;

  const { buildClass, joinClasses } = useClasses('RecoveryPlanBarGraphStaff', {
    'goal-achieved': goalAchievementState === 'GOAL_ACHIEVED',
    'goal-not-achieved': goalAchievementState === 'GOAL_NOT_ACHIEVED',
    'goal-not-set': goalAchievementState === 'GOAL_NOT_SET',
    'value-set': value != null,
    'value-full-width': (Math.min(FULL_WIDTH_BAR_OFFSET, valueProportion) * 100) === FULL_WIDTH_BAR_OFFSET,
    readonly,
    editable: isEditable,
    today: isToday,
  });

  const CSSCustomProperties = {
    '--recovery-plan-graph-value-offset': `${
      Math.min(FULL_WIDTH_BAR_OFFSET, valueProportion) * 100
    }%`,
    '--recovery-plan-graph-goal-offset': `${
      Math.min(FULL_WIDTH_BAR_OFFSET, goalProportion) * 100
    }%`,
  } as React.CSSProperties;

  const registrationLink = `${routes.dailyRegistration}?date=${moment(date).format('YYYY-MM-DD')}`;

  return (
    <div className={buildClass()} style={CSSCustomProperties}>
      {isEditable && (
        <CoreLink className={buildClass('add-registration-link')} to={registrationLink} aria-describedby={`date-label-${date.toISOString()}`}>
          <span className="sr-only">{formattedDayOfWeek}</span>
        </CoreLink>
      )}
      <div className={buildClass('day-of-week')}>
        <div className={buildClass('day-of-week-value')}>
          {formattedDayOfWeek}
        </div>
        <span className="sr-only" id={`date-label-${date.toISOString()}`} aria-hidden>{formattedDate}</span>
      </div>
      <div className={buildClass('inner')}>
        {goal != null && (
          <div className={buildClass('goal-indicator')}>{goal}</div>
        )}
        <div className={buildClass('value')} />
      </div>
      <div className={joinClasses(buildClass('buffer-area'), 'icon-after')} />
    </div>
  );
};

export default BarGraphStaff;
